import React, { useEffect, useState, useCallback } from 'react';
import { getRouteWeek, moveStopToShift, approveRoute, unapproveRoute } from './api';
import { useTranslation } from '../i18n';

const SLOT = {
  morning: { color: '#f59e0b', bg: '#fffdf5', head: '#fef3c7', text: '#92400e', icon: '☀️' },
  afternoon: { color: '#2c6e9e', bg: '#f7fbff', head: '#ecf3fb', text: '#1e5e9c', icon: '🌤️' },
  evening: { color: '#7b3fb0', bg: '#fdfaff', head: '#f3eafa', text: '#6b30a3', icon: '🌙' }
};
const SHIFTS = ['morning', 'afternoon', 'evening'];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayYmd = () => ymd(new Date());

export default function WeekView({ role, onOpenRoute }) {
  const { t, lang } = useTranslation();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const slotLabel = (s) => t(`cal.${s}`);
  const canEdit = role === 'owner' || role === 'manager';

  // Week starts on Sunday (matches the month grid).
  const [weekStart, setWeekStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); return d; });
  const [routes, setRoutes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [drag, setDrag] = useState(null); // { stopId, fromDate, fromSlot }
  const [over, setOver] = useState(null); // `${date}|${slot}`

  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
  const from = ymd(days[0]);
  const to = ymd(days[6]);

  const load = useCallback(async () => {
    try {
      const { data } = await getRouteWeek(from, to);
      setRoutes(data || []);
      setError('');
    } catch (e) {
      setError(t('cal.err_week') || 'Could not load the week.');
    }
  }, [from, to, t]);

  useEffect(() => { load(); }, [load]);

  // routes indexed by `${date}|${slot}`
  const byCell = {};
  routes.forEach((r) => { byCell[`${r.route_date}|${r.time_slot}`] = r; });

  const drop = async (date, slot) => {
    setOver(null);
    if (!drag) return;
    const { stopId, fromDate, fromSlot } = drag;
    setDrag(null);
    if (fromDate === date && fromSlot === slot) return;
    setBusy(true);
    try {
      await moveStopToShift(stopId, slot, date);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || t('cal.err_move'));
      await load(); // reconcile the board after a failed move
    }
    setBusy(false);
  };

  const toggleApprove = async (r) => {
    setBusy(true);
    try {
      if (r.approved) await unapproveRoute(r.id);
      else if ((r.route_stops || []).length > 0) await approveRoute(r.id);
      await load();
    } catch (e) { setError(t('cal.err_update')); }
    setBusy(false);
  };

  const weekLabel = `${days[0].toLocaleDateString(locale, { month: 'short', day: 'numeric' })} – ${days[6].toLocaleDateString(locale, { month: 'short', day: 'numeric' })}`;

  return (
    <div>
      {error && (
        <div
          style={styles.error}
          role="alert"
          tabIndex={0}
          onClick={() => setError('')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setError(''); } }}
        >
          {error}
        </div>
      )}

      <div style={styles.nav}>
        <button
          style={styles.arrow}
          aria-label={t('a11y.previous_week')}
          onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}
        >
          <span aria-hidden="true">‹</span>
        </button>
        <span style={styles.weekLabel} aria-live="polite">{weekLabel}</span>
        <button
          style={styles.arrow}
          aria-label={t('a11y.next_week')}
          onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}
        >
          <span aria-hidden="true">›</span>
        </button>
        <button style={styles.todayBtn} onClick={() => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); setWeekStart(d); }}>{t('cal.today')}</button>
        {canEdit && <span style={styles.dragHint}>{t('cal.week_hint')}</span>}
      </div>

      <div style={styles.board} role="grid" aria-label={weekLabel}>
        {days.map((d) => {
          const date = ymd(d);
          const isToday = date === todayYmd();
          const dayLabelText = d.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' });
          return (
            <div key={date} style={styles.col} role="row">
              <button
                style={{ ...styles.colHead, ...(isToday ? styles.colHeadToday : {}) }}
                onClick={() => onOpenRoute && onOpenRoute(date)}
                title={t('cal.open_route')}
                aria-label={`${dayLabelText}${isToday ? ' — ' + t('cal.today') : ''}`}
              >
                <span style={styles.dow} aria-hidden="true">{d.toLocaleDateString(locale, { weekday: 'short' })}</span>
                <span style={styles.dnum} aria-hidden="true">{d.getDate()}</span>
              </button>

              {SHIFTS.map((slot) => {
                const r = byCell[`${date}|${slot}`];
                const stops = r ? (r.route_stops || []).slice().sort((a, b) => a.stop_number - b.stop_number) : [];
                const meta = SLOT[slot];
                const isOver = over === `${date}|${slot}`;
                return (
                  <div
                    key={slot}
                    role="region"
                    aria-label={t('a11y.drop_zone_day', { day: `${dayLabelText} — ${slotLabel(slot)}` })}
                    style={{ ...styles.lane, background: meta.bg, ...(isOver ? styles.laneOver : {}) }}
                    onDragOver={(e) => { if (canEdit && drag) { e.preventDefault(); setOver(`${date}|${slot}`); } }}
                    onDragLeave={() => setOver(null)}
                    onDrop={() => canEdit && drop(date, slot)}
                  >
                    <div style={styles.laneHead}>
                      <span style={{ ...styles.laneIcon }} aria-hidden="true">{meta.icon}</span>
                      <span style={{ ...styles.laneName, color: meta.text }}>{slotLabel(slot)}</span>
                      <span style={{ ...styles.laneCount, background: meta.color }} aria-label={`${stops.length} ${stops.length === 1 ? t('cal.stops_one', { n: stops.length }) : t('cal.stops_many', { n: stops.length })}`}>{stops.length}</span>
                      {canEdit && r && stops.length > 0 && (
                        <button
                          style={{ ...styles.approveDot, ...(r.approved ? styles.approveDotOn : {}) }}
                          aria-label={r.approved ? t('cal.unapprove') : t('cal.approve')}
                          aria-pressed={r.approved}
                          title={r.approved ? t('cal.unapprove') : t('cal.approve')}
                          disabled={busy}
                          onClick={() => toggleApprove(r)}
                        >
                          <span aria-hidden="true">{r.approved ? '✓' : '○'}</span>
                        </button>
                      )}
                    </div>
                    {stops.length === 0 && <div style={styles.laneEmpty} aria-hidden="true">{isOver ? slotLabel(slot) : '—'}</div>}
                    {stops.map((s) => (
                      <div
                        key={s.id}
                        draggable={canEdit}
                        role="listitem"
                        aria-label={`${s.clients?.name || 'Client'}${s.completed ? ' — ' + t('cal.approved') : ''}`}
                        onDragStart={(e) => {
                          // Safari/Firefox require setData to start the drag.
                          e.dataTransfer.setData('text/plain', s.id);
                          e.dataTransfer.effectAllowed = 'move';
                          setDrag({ stopId: s.id, fromDate: date, fromSlot: slot });
                        }}
                        onDragEnd={() => { setDrag(null); setOver(null); }}
                        style={{ ...styles.card, ...(canEdit ? styles.cardDraggable : {}), ...(drag && drag.stopId === s.id ? styles.cardDragging : {}) }}
                        title={canEdit ? t('cal.week_card_hint') : undefined}
                      >
                        <div style={styles.cardName}>{s.clients?.name || 'Client'}{s.completed ? <span aria-hidden="true"> ✓</span> : ''}</div>
                        <div style={styles.cardAddr}>{s.clients?.street || ''}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {canEdit && <div style={styles.autosave} role="status" aria-live="polite"><span aria-hidden="true">✓ </span>{t('cal.autosaved')}</div>}
    </div>
  );
}

const styles = {
  error: { background: '#FEF2F2', color: '#dc2626', borderRadius: 8, padding: '10px 12px', fontSize: 13, cursor: 'pointer', marginBottom: 12, border: '1px solid #f3c0c0', position: 'sticky', top: 8, zIndex: 30 },
  nav: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' },
  arrow: { width: 32, height: 32, border: '1px solid #e3e9df', borderRadius: 8, background: '#fff', color: '#14653a', fontSize: 16, fontWeight: 800, cursor: 'pointer' },
  weekLabel: { fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 17, color: '#1a1a1a', minWidth: 150 },
  todayBtn: { fontSize: 12, fontWeight: 700, color: '#5f6b58', border: '1px solid #e3e9df', borderRadius: 8, padding: '6px 12px', background: '#fff', cursor: 'pointer' },
  dragHint: { fontSize: 12, color: '#5f6b58', marginLeft: 'auto' },

  board: { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(150px, 1fr))', gap: 8, overflowX: 'auto', paddingBottom: 6 },
  col: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 150 },
  colHead: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, padding: '8px 0', borderRadius: 10, border: '1px solid #e3e9df', background: '#fff', cursor: 'pointer', color: '#1a1a1a' },
  colHeadToday: { background: '#14653a', border: '1px solid #14653a', color: '#fff' },
  dow: { fontSize: 11, fontWeight: 700, color: 'inherit', textTransform: 'uppercase', letterSpacing: '0.04em' },
  dnum: { fontFamily: 'Manrope, sans-serif', fontSize: 16, fontWeight: 800 },

  lane: { borderRadius: 10, border: '1px solid #eef1ec', padding: 6, minHeight: 78, display: 'flex', flexDirection: 'column', gap: 5, transition: 'box-shadow .12s, border-color .12s' },
  laneOver: { borderColor: '#14653a', boxShadow: '0 0 0 2px rgba(20,101,58,0.25) inset' },
  laneHead: { display: 'flex', alignItems: 'center', gap: 5 },
  laneIcon: { fontSize: 12 },
  laneName: { fontSize: 11, fontWeight: 700, flex: 1 },
  laneCount: { color: '#fff', fontSize: 10, fontWeight: 800, borderRadius: 100, padding: '1px 6px' },
  approveDot: { width: 20, height: 20, borderRadius: '50%', border: '1px solid #cfe9d4', background: '#fff', color: '#5f6b58', fontSize: 11, cursor: 'pointer', padding: 0, lineHeight: 1 },
  approveDotOn: { background: '#14653a', borderColor: '#14653a', color: '#fff', fontWeight: 800 },
  laneEmpty: { fontSize: 11, color: '#5f6b58', textAlign: 'center', padding: '6px 0' },
  card: { background: '#fff', border: '1px solid #e3e9df', borderRadius: 8, padding: '6px 8px' },
  cardDraggable: { cursor: 'grab' },
  cardDragging: { opacity: 0.4 },
  cardName: { fontSize: 12, fontWeight: 700, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  cardAddr: { fontSize: 10.5, color: '#5f6b58', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  autosave: { fontSize: 12, color: '#14653a', fontWeight: 600, marginTop: 12 }
};
